/**
 * Operations Dashboard Types
 *
 * TypeScript interfaces for the operations dashboard metrics.
 */

import type { ProjectStatus, ProjectType } from '../operations-projects/types';

// =============================================================================
// Task Types (from operations-tasks)
// =============================================================================

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

// =============================================================================
// Dashboard Summary Types
// =============================================================================

export interface ProjectsSummary {
  total: number;
  active: number;
  completed: number;
  by_status: Record<ProjectStatus, number>;
  by_type: Record<ProjectType, number>;
  total_amount: number;
}

export interface TasksSummary {
  total: number;
  completed: number;
  overdue: number;
  by_status: Record<TaskStatus, number>;
  by_priority: Record<TaskPriority, number>;
  unassigned: number;
}

export interface CompletedCounts {
  projects: number;
  tasks: number;
}

export interface DashboardSummary {
  projects: ProjectsSummary;
  tasks: TasksSummary;
  completed_this_week: CompletedCounts;
  completed_this_month: CompletedCounts;
}

// =============================================================================
// Workload Types
// =============================================================================

export interface WorkloadItem {
  assignee_id: string;
  assignee_name: string;
  task_count: number;
  overdue_count: number;
  in_progress_count: number;
  todo_count: number;
  blocked_count: number;
}

// =============================================================================
// Timeline Types
// =============================================================================

export interface TimelineItem {
  id: string;
  type: 'project' | 'task';
  title: string;
  due_date: string;
  status: string;
  project_type?: ProjectType;
  priority?: TaskPriority;
  assignee_name?: string;
}

// =============================================================================
// Activity Types
// =============================================================================

export interface ActivityItem {
  id: string;
  entity_type: 'project' | 'task' | 'quote';
  entity_id: string;
  entity_title: string;
  action: string;
  details?: Record<string, unknown>;
  actor_name: string;
  created_at: string;
}

// =============================================================================
// V4 API Response Types (camelCase from API)
// =============================================================================

export interface V4ProjectsSummary {
  total: number;
  active: number;
  completed: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  totalAmount: number;
}

export interface V4TasksSummary {
  total: number;
  completed: number;
  overdue: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  unassigned: number;
}

export interface V4DashboardSummary {
  projects: V4ProjectsSummary;
  tasks: V4TasksSummary;
  completedThisWeek: CompletedCounts;
  completedThisMonth: CompletedCounts;
}

export interface V4WorkloadItem {
  assigneeId: string;
  assigneeName: string;
  taskCount: number;
  overdueCount: number;
  inProgressCount: number;
  todoCount: number;
  blockedCount: number;
}

export interface V4TimelineItem {
  id: string;
  type: 'project' | 'task';
  title: string;
  dueDate: string;
  status: string;
  projectType?: string;
  priority?: string;
  assigneeName?: string;
}

export interface V4ActivityItem {
  id: string;
  entityType: 'project' | 'task' | 'quote';
  entityId: string;
  entityTitle: string;
  action: string;
  details?: Record<string, unknown>;
  actorName: string;
  createdAt: string;
}

// ============================================================
// Projects Kanban Types
// ============================================================

export const UNIVERSAL_STAGES = [
  { key: 'new_purchase_order', label: 'New Purchase Order', valuePercent: 10, responsibleRole: 'Account Manager' },
  { key: 'planification', label: 'Planification', valuePercent: 20, responsibleRole: 'Account Manager, Ops Coordination' },
  { key: 'picking_packing', label: 'Picking & Packing', valuePercent: 30, responsibleRole: 'Fulfillment Technician' },
  { key: 'platform_configuration', label: 'Hardware & Platform Configuration', valuePercent: 40, responsibleRole: 'Fulfillment Technician, Ops Coordination' },
  { key: 'ready_to_ship', label: 'Ready to Ship', valuePercent: 45, responsibleRole: 'Fulfillment Technician, Ops Coordination' },
  { key: 'shipping', label: 'Shipping', valuePercent: 50, responsibleRole: 'Fulfillment Technician' },
  { key: 'installation_calibration', label: 'Installation/Calibration', valuePercent: 70, responsibleRole: 'Ops Coordination' },
  { key: 'invoicing', label: 'Invoicing', valuePercent: 80, responsibleRole: 'Ops Coordination' },
  { key: 'customer_onboarding', label: 'Customer Onboarding', valuePercent: 90, responsibleRole: 'Account Manager' },
  { key: 'done', label: 'Done', valuePercent: 100, responsibleRole: '' },
] as const;

export type StageKey = typeof UNIVERSAL_STAGES[number]['key'];

export interface ProjectKanbanCard {
  id: string;
  quoteId?: string;
  supportProjectId?: string | null;
  projectNumber: string;
  quoteNumber?: string | null;
  quoteTitle?: string;
  projectName: string;
  type: string;
  commercialStatus?: string;
  /** Operations triage priority; null = never set (no badge). */
  priority?: 'urgent' | 'normal' | 'low' | null;
  accountId?: string | null;
  accountName: string;
  amount: number;
  currency?: string;
  sensorsQty: number;
  assignedToName: string;
  accountManagerName?: string;
  scheduledDate: string | null;
  position: number;
  currentStage: StageKey;
  milestoneProgress: string;
}

export interface KanbanColumnTotals {
  projectCount: number;
  sensorsQty: number;
  value: number;
}

export interface KanbanColumn {
  stage: StageKey;
  label: string;
  valuePercent: number;
  responsibleRole: string;
  projects: ProjectKanbanCard[];
  totals: KanbanColumnTotals;
}

export interface QuoteStatusTotal {
  status: string;
  count: number;
  amount: number;
}

export interface ProjectsKanbanData {
  columns: KanbanColumn[];
  totalCount: number;
  /**
   * $ totals for the three summary tiles, keyed approved / inprogress /
   * invoiced. These bucket the fulfillment STAGES (Approved = New Purchase
   * Order; In Progress = Planification…Invoicing; Invoiced = Customer
   * Onboarding + Done), not the raw commercial status, so the tiles reconcile
   * with the columns. Computed over the same creation-windowed quotes the board
   * shows — or all active quotes when no window is set. Present on
   * /v4/quotes/kanban.
   */
  statusSummary?: QuoteStatusTotal[];
}

export interface KanbanQueryParams {
  filter?: 'this_week' | 'this_month' | 'all';
  type?: string;
  assignee?: string;
  accountId?: string;
  /**
   * Inclusive quote-CREATION-date window (YYYY-MM-DD). Filters the board, the
   * status-$ summary, and (derived client-side) the weighted pipeline. Either
   * bound may be omitted for an open-ended window.
   */
  fromDate?: string;
  toDate?: string;
}

export interface AdvanceStageInput {
  quoteId: string;
  targetStage: StageKey;
  /**
   * Required when moving a card BACKWARD to an earlier stage. Recorded, with the
   * acting user, in the append-only quote_operations_audit_logs so every
   * regression is traceable. Omitted for forward moves.
   */
  reason?: string;
}

export interface UpdateProjectPositionsInput {
  updates: Array<{ id: string; position: number }>;
}
